import { useEffect, useState } from 'react';
import { FEATURE_KEYS, setFeatures } from './features';
import { getCatalog, saveCatalog } from './catalog';

// industry profiles (one active at a time) -> feature groups, catalogue sector, allowed pipe types
const KEY = 'giss_profiles';
const SUB_KEY = 'giss_detect_sub';
const VER_KEY = 'giss_profiles_v';
const DET = ['network', 'fittings', 'gnss', 'route', 'proj', 'cadio', 'survey', 'locate'];
export const DETECT_SUBS = {
  water: { icon: '💧', feats: [...DET, 'loggers', 'leak', 'leakCalc', 'pressure', 'hydrant', 'corr', 'tracer'], sector: 'water', types: ['water', 'fire', 'heat', 'other'] },
  gas: { icon: '🔥', feats: [...DET, 'leak', 'gas', 'kks', 'tracks'], sector: 'gas', types: ['gas', 'other'] },
  cable: { icon: '⚡', feats: [...DET, 'fault'], sector: 'power', types: ['power', 'telecom', 'other'] },
  sewer: { icon: '🕳️', feats: [...DET, 'sewer', 'leak', 'studio'], sector: 'sewer', types: ['sewer', 'other'] },
  inhouse: { icon: '🏠', feats: [...DET, 'leak', 'leakCalc', 'pressure', 'tracer', 'studio', 'inventory'], sector: 'inhouse', types: ['water', 'gas', 'heat', 'sewer', 'other'] },
  locate: { icon: '📡', feats: [...DET, 'util', 'pits', 'xbear', 'stake'], sector: 'net' },
  heat: { icon: '♨️', feats: [...DET, 'leak', 'fault', 'pressure', 'tracer', 'studio'], sector: 'heat', types: ['heat', 'other'] },
  roof: { icon: '🏢', feats: [...DET, 'leak', 'tracer', 'studio'], sector: 'roof', types: ['sewer', 'other'] },
};
export const PROFILES = {
  detect: { icon: '🔎', feats: DET },
  pipe: { icon: '🚜', feats: ['network', 'fittings', 'leak', 'util', 'sewer', 'maint', 'pits', 'survey', 'gnss', 'stake', 'lv', 'inventory', 'route', 'costs', 'studio', 'cadio'], sector: 'net' },
  survey: { icon: '📐', feats: ['network', 'fittings', 'survey', 'gnss', 'stake', 'cadio', 'studio', 'proj', 'xbear', 'route'], sector: 'survey' },
  garden: { icon: '🌱', feats: ['network', 'fittings', 'gnss', 'stake', 'tracks', 'pressure', 'maint', 'garden', 'studio', 'costs', 'route', 'xbear'], sector: 'garden', types: ['water', 'other'] },
  building: { icon: '🧱', feats: ['scaffold', 'facade', 'inventory', 'studio', 'costs', 'lv', 'route'], sector: 'building' },
  aqua: { icon: '🌊', feats: ['network', 'fittings', 'gnss', 'aqua', 'pressure', 'hydrant', 'maint', 'studio', 'cadio', 'proj', 'route'], sector: 'water', types: ['water', 'fire', 'other'] },
  storm: { icon: '🌧️', feats: ['network', 'fittings', 'gnss', 'aqua', 'sewer', 'maint', 'studio', 'cadio', 'proj', 'route'], sector: 'sewer', types: ['sewer', 'other'] },
  outdoor: { icon: '🥾', feats: ['tracks', 'proj', 'xbear'] },
  photo: { icon: '📸', feats: ['gnss', 'cadio'], types: ['other'] },
  all: { icon: '🔧', feats: FEATURE_KEYS },
};
export const getProfiles = () => { try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; } };
export const getMode = () => { const p = getProfiles()?.[0]; return PROFILES[p] ? p : 'all'; };
export const getSub = () => (DETECT_SUBS[localStorage.getItem(SUB_KEY)] ? localStorage.getItem(SUB_KEY) : 'water');
export const modeId = (m = getMode()) => (m === 'detect' ? `detect_${getSub()}` : m);
const conf = (m) => (m === 'detect' ? { ...PROFILES.detect, ...DETECT_SUBS[getSub()] } : PROFILES[m]);
// existing installations keep the full view
const FRESH = !localStorage.getItem('giss_first_run') || !!sessionStorage.getItem('giss_fresh_keep');
export const needsOnboarding = () => FRESH && !getProfiles();
const featsOf = (m) => { const on = new Set(conf(m)?.feats || []); return Object.fromEntries(FEATURE_KEYS.map((k) => [k, on.has(k)])); };
export function applyProfiles(list, sub) {
  const m = PROFILES[list?.[0]] ? list[0] : 'all';
  if (sub && DETECT_SUBS[sub]) localStorage.setItem(SUB_KEY, sub);
  localStorage.setItem(KEY, JSON.stringify([m]));
  localStorage.setItem(VER_KEY, '10');
  setFeatures(featsOf(m));
  const cat = getCatalog(), sec = conf(m).sector;
  saveCatalog({ ...cat, sector: m === 'all' || !sec ? cat.sector : sec });
  window.dispatchEvent(new Event('giss-profiles'));
}
export const setSub = (sub) => applyProfiles(['detect'], sub);
// update from older profile sets: re-apply the stored profile once (pipe = Tiefbau)
if (getProfiles()?.length && localStorage.getItem(VER_KEY) !== '10') applyProfiles([getMode()]);
// crew start mode: ?mode=detect_water link/QR applies it on a new phone; stored start mode wins on every app start
const START_KEY = 'giss_start_mode';
const validId = (id) => { const [m, s] = String(id || '').split('_'); return PROFILES[m] && (!s || (m === 'detect' && DETECT_SUBS[s])) ? [m, s] : null; };
export const getStart = () => (validId(localStorage.getItem(START_KEY)) ? localStorage.getItem(START_KEY) : null);
export const setStart = (id) => { if (id) localStorage.setItem(START_KEY, id); else localStorage.removeItem(START_KEY); };
// per-mode tool favourites (max. 3), shared together with the start-mode link
const FAV_KEY = 'giss_tool_favs';
export const readFavs = () => { try { return JSON.parse(localStorage.getItem(FAV_KEY)) || {}; } catch { return {}; } };
export const setFavsFor = (id, list) => { localStorage.setItem(FAV_KEY, JSON.stringify({ ...readFavs(), [id]: list })); window.dispatchEvent(new Event('giss-favs')); };
const FAV_PRE = 'fs-tool-';
export const startLink = (id, favs = [], cat = null) => `${window.location.origin}/?mode=${id}${favs.length ? `&fav=${favs.map((f) => f.replace(FAV_PRE, '')).join(',')}` : ''}${cat ? `&cat=${cat}` : ''}`;
// link opened on a phone -> pending until the user confirms (StartLinkPrompt)
const PEND_KEY = 'giss_link_pending';
export const getPending = () => { try { const p = JSON.parse(localStorage.getItem(PEND_KEY)); return p && validId(p.mode) ? p : null; } catch { return null; } };
export function acceptPending(ok) {
  const p = getPending();
  localStorage.removeItem(PEND_KEY);
  if (!ok || !p) return;
  const [m, s] = validId(p.mode);
  setStart(p.mode);
  applyProfiles([m], s);
  if (p.fav.length) setFavsFor(p.mode, p.fav);
}
(() => {
  const qs = new URLSearchParams(window.location.search), q = qs.get('mode');
  if (validId(q)) {
    const fav = (qs.get('fav') || '').split(',').filter((x) => /^[a-z0-9-]+$/.test(x)).slice(0, 3).map((x) => FAV_PRE + x);
    const cat = /^[A-Z0-9]{6}$/.test(qs.get('cat') || '') ? qs.get('cat') : null;
    localStorage.setItem(PEND_KEY, JSON.stringify({ mode: q, fav, cat }));
    window.history.replaceState(null, '', window.location.pathname);
  }
  const st = validId(getStart());
  if (st && modeId() !== getStart()) applyProfiles([st[0]], st[1]);
})();
// texts with branch variants (key_survey, key_detect_inhouse …) for exports
export const modeT = (t) => new Proxy(t, { get: (o, k) => (typeof k === 'string' && (o[`${k}_${modeId()}`] ?? o[`${k}_${getMode()}`])) ?? o[k] });
export const openProfiles = () => window.dispatchEvent(new Event('giss-profile-open'));
export function useMode() {
  const [m, setM] = useState(modeId);
  useEffect(() => { const on = () => setM(modeId()); window.addEventListener('giss-profiles', on); return () => window.removeEventListener('giss-profiles', on); }, []);
  return m;
}
// profile-specific label: t[key_detect_gas] -> t[key_detect] -> t[key]
export const ml = (t, key) => { const id = modeId(), m = getMode(); return t[`${key}_${id}`] || t[`${key}_${m}`] || t[key]; };
export const allowedTypes = () => conf(getMode())?.types || null;
// sewer mode: only sewer-related point types (legend, type selects)
export const SEWER_FITS = ['manhole', 'house', 'drain', 'leak', 'tee3', 'gg', 'gk', 'other'];
export const isSewer = () => modeId() === 'detect_sewer' || modeId() === 'storm';
// projects of the former AquaFlow sewer module (sector 'aqua', only sewer pipes) belong to Stormwater Management
const sewP = (p) => p.type === 'sewer' || p.med === 'sewer' || p.med === 'rain';
export const secOf = (p) => (p?.sector === 'aqua' && (p.pipes || []).length && p.pipes.every(sewP) ? 'storm' : p?.sector);
// mixed AquaFlow projects (water + sewer) can be opened in both branches
export const inMode = (p, m) => secOf(p) === m || (m === 'storm' && p?.sector === 'aqua' && (p.pipes || []).some(sewP));
const SUB_FITS = {
  detect_gas: ['valve', 'house', 'leak', 'kks', 'gmic', 'locate', 'manhole', 'other'],
  detect_cable: ['cjoint', 'cabinet', 'manhole', 'cfault', 'locate', 'tracer', 'other'],
  detect_inhouse: ['valve', 'meter', 'leak', 'tracer', 'gmic', 'other'],
  detect_locate: ['valve', 'hydrant', 'manhole', 'house', 'cjoint', 'cabinet', 'locate', 'tracer', 'other'],
  detect_heat: ['valve', 'manhole', 'sleeve', 'lws', 'leak', 'air', 'drain', 'tracer', 'house', 'other'],
  detect_roof: ['gully', 'dome', 'vent', 'leak', 'tracer', 'other'],
  survey: ['other'],
  detect_water: ['valve', 'hydrant', 'house', 'meter', 'air', 'drain', 'cross4', 'tee3', 'manhole', 'source', 'prv', 'leak', 'gmic', 'tracer', 'locate', 'other'],
  pipe: ['valve', 'hydrant', 'manhole', 'house', 'air', 'drain', 'cross4', 'tee3', 'meter', 'gg', 'gk', 'cjoint', 'cabinet', 'sleeve', 'source', 'prv', 'leak', 'other'],
  photo: ['other'],
  aqua: ['node', 'source', 'pump', 'valve', 'prv', 'hydrant', 'house', 'cross4', 'tee3', 'meter', 'air', 'drain', 'manhole', 'other'],
};
export const fitTypes = () => (isSewer() ? SEWER_FITS : SUB_FITS[modeId()] || null);
